import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DashboardConfig } from '@trinity/chat/types/dashboard-config';
import { useSegmentPageBuilderApi } from './api';
import type { PageWithConfig, PageVersion } from './api';

export const segmentPageQueryKey = (projectId: number, segmentId: number): string[] =>
  ['segment-page', String(projectId), String(segmentId)];

export const segmentPageVersionsQueryKey = (projectId: number, segmentId: number): string[] =>
  ['segment-page-versions', String(projectId), String(segmentId)];

export function useSegmentPage(projectId: number, segmentId: number): ReturnType<typeof useQuery<PageWithConfig | null>> {
  const api = useSegmentPageBuilderApi();
  return useQuery({
    queryKey: segmentPageQueryKey(projectId, segmentId),
    queryFn: () => api.getSegmentPage(projectId, segmentId),
    enabled: !!projectId && !!segmentId,
    refetchOnWindowFocus: false,
  });
}

export function useSegmentPageVersions(projectId: number, segmentId: number): ReturnType<typeof useQuery<PageVersion[]>> {
  const api = useSegmentPageBuilderApi();
  return useQuery({
    queryKey: segmentPageVersionsQueryKey(projectId, segmentId),
    queryFn: () => api.getPageVersions?.(projectId, segmentId) ?? Promise.resolve([]),
    enabled: !!projectId && !!segmentId,
  });
}

export function useSavePageVersion(projectId: number, segmentId: number): ReturnType<typeof useMutation<PageVersion, Error, { config: DashboardConfig; description?: string }>> {
  const api = useSegmentPageBuilderApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { config: DashboardConfig; description?: string }) =>
      api.savePageVersion(projectId, segmentId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: segmentPageQueryKey(projectId, segmentId) });
      void queryClient.invalidateQueries({ queryKey: segmentPageVersionsQueryKey(projectId, segmentId) });
    },
  });
}

export function useRevertPageVersion(projectId: number, segmentId: number): ReturnType<typeof useMutation<PageVersion, Error, { versionId: number }>> {
  const api = useSegmentPageBuilderApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { versionId: number }) =>
      api.revertPageVersion(projectId, segmentId, data.versionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: segmentPageQueryKey(projectId, segmentId) });
      void queryClient.invalidateQueries({ queryKey: segmentPageVersionsQueryKey(projectId, segmentId) });
    },
  });
}
